/**
 * SRES · Global stage-tracking sentinel (Iter82 · Feb 2026).
 *
 * Mounted once at the AppShell so every route in the 9-step flow
 * accrues time — including the non-wizard-shell landing pages
 * (Project Details `/setup`, Logs & Maps `/logs-maps`, Download
 * `/reports`).
 *
 * Renders nothing; runs a small effect that:
 *   1. Resolves the current stage from the URL via `resolveFlowKey`.
 *   2. Starts a visit timer on that stage.
 *   3. Flushes every 15 s so the sidebar chips update in near real-time.
 *   4. Stops the timer on unmount / URL change / tab-hide.
 */
import { useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { resolveFlowKey } from "@/config/simplified_flow";
import { startStageVisit } from "@/lib/stageActuals";

const FLUSH_MS = 15_000;

export default function StageTracker() {
  const { pid } = useParams();
  const { pathname } = useLocation();
  const stopRef = useRef(null);

  useEffect(() => {
    if (!pid) return;
    const stageKey = resolveFlowKey(pathname);
    if (!stageKey) return;

    // start the visit + a periodic flush so the localStorage entry
    // grows even before the user leaves the page.
    const stop = startStageVisit(pid, stageKey);
    stopRef.current = stop;

    // Periodic flush — stop() is idempotent-first-call. We restart the
    // visit after each flush so the accumulator keeps growing.
    let running = true;
    let localStop = stop;
    const iv = setInterval(() => {
      if (!running) return;
      localStop();
      localStop = startStageVisit(pid, stageKey);
      stopRef.current = localStop;
    }, FLUSH_MS);

    // Persist on tab-hide (pagehide is more reliable than beforeunload
    // for mobile Safari + PWA scenarios).
    const onHide = () => { try { localStop(); } catch { /* silent */ } };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });

    return () => {
      running = false;
      clearInterval(iv);
      window.removeEventListener("pagehide", onHide);
      try { localStop(); } catch { /* silent */ }
    };
  }, [pid, pathname]);

  return null;
}
